import { useTimelineStore } from '@/features/timeline/stores/timeline-store'
import { updateItem } from '@/features/timeline/stores/actions/item-actions'
import type { TimelineItem } from '@/types/timeline'

export type CaptionStyleUpdate = {
  fontFamily?: string
  fontSize?: number
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold'
  color?: string
  backgroundColor?: string
  backgroundRadius?: number
  textAlign?: 'left' | 'center' | 'right'
  verticalAlign?: 'top' | 'middle' | 'bottom'
  lineHeight?: number
  letterSpacing?: number
  textPadding?: number
  stroke?: { width: number; color: string }
  textShadow?: { offsetX: number; offsetY: number; blur: number; color: string }
}

function isCaptionTextItem(item: TimelineItem): boolean {
  return (
    item.type === 'text' &&
    (item.textRole === 'caption' || item.captionSource !== undefined)
  )
}

function matchesCaptionSource(
  item: TimelineItem & { captionSource?: { type?: string; clipId?: string; mediaId?: string } },
  source?: {
    mediaId?: string
    clipIds?: string[]
    captionSourceType?: string
  },
): boolean {
  if (!source) {
    return true
  }

  if (source.mediaId && item.captionSource?.mediaId !== source.mediaId) {
    return false
  }

  if (source.clipIds?.length && item.captionSource?.clipId) {
    if (!source.clipIds.includes(item.captionSource.clipId)) {
      return false
    }
  }

  if (source.captionSourceType && item.captionSource?.type !== source.captionSourceType) {
    return false
  }

  return true
}

function buildStyleUpdates(style: CaptionStyleUpdate): Partial<TimelineItem> {
  const updates: Record<string, unknown> = {}

  if (style.fontFamily !== undefined) updates.fontFamily = style.fontFamily
  if (style.fontSize !== undefined) updates.fontSize = style.fontSize
  if (style.fontWeight !== undefined) updates.fontWeight = style.fontWeight
  if (style.color !== undefined) updates.color = style.color
  if (style.backgroundColor !== undefined) updates.backgroundColor = style.backgroundColor
  if (style.backgroundRadius !== undefined) updates.backgroundRadius = style.backgroundRadius
  if (style.textAlign !== undefined) updates.textAlign = style.textAlign
  if (style.verticalAlign !== undefined) updates.verticalAlign = style.verticalAlign
  if (style.lineHeight !== undefined) updates.lineHeight = style.lineHeight
  if (style.letterSpacing !== undefined) updates.letterSpacing = style.letterSpacing
  if (style.textPadding !== undefined) updates.textPadding = style.textPadding
  if (style.stroke !== undefined) updates.stroke = style.stroke
  if (style.textShadow !== undefined) updates.textShadow = style.textShadow

  return updates as Partial<TimelineItem>
}

export async function replaceCaptionStyle(input: {
  source?: {
    mediaId?: string
    clipIds?: string[]
    captionSourceType?: 'transcript' | 'ai-captions' | 'subtitle-import' | 'embedded-subtitles'
  }
  style: CaptionStyleUpdate
  dryRun?: boolean
}): Promise<{
  matchedItemCount: number
  updatedItemCount: number
  dryRun: boolean
}> {
  const timelineItems = useTimelineStore.getState().items

  // Find target caption items
  const targetItems = timelineItems.filter((item) => {
    if (!isCaptionTextItem(item)) {
      return false
    }

    return matchesCaptionSource(
      item as TimelineItem & {
        captionSource?: { type?: string; clipId?: string; mediaId?: string }
      },
      input.source,
    )
  })

  const matchedItemCount = targetItems.length
  const styleUpdates = buildStyleUpdates(input.style)
  const hasStyleChanges = Object.keys(styleUpdates).length > 0

  if (!hasStyleChanges || matchedItemCount === 0 || input.dryRun) {
    return { matchedItemCount, updatedItemCount: 0, dryRun: input.dryRun ?? false }
  }

  // Apply style to each caption item
  for (const item of targetItems) {
    updateItem(item.id, styleUpdates)
  }

  return { matchedItemCount, updatedItemCount: matchedItemCount, dryRun: false }
}
